import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Button } from 'components/ui';
import { LoadingState, ErrorState, EmptyState } from 'components/feedback';
import { BoxIcon, PlusIcon } from 'components/icons';
import { ROUTES, catalogEditPath } from 'lib/constants';
import { useVendorCatalog } from '../hooks/useVendorCatalog';
import { CatalogToolbar } from '../components/CatalogToolbar';
import { CatalogItemCard } from '../components/CatalogItemCard';
import { CatalogFilters, catalogItemMatches } from '../components/CatalogFilters';

/** Catalog listing page with top filters + client-side search. */
export function CatalogPage() {
  const navigate = useNavigate();
  const { items, loading, error, refetch } = useVendorCatalog();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState('all');

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      if (!catalogItemMatches(item, { category, status })) return false;
      if (!query) return true;
      const haystack = [item.vendorProductName, item.name, item.brand, item.category, item.subcategory]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [items, search, category, status]);

  let content;
  if (loading && items.length === 0) {
    content = <LoadingState label="Loading your catalog…" />;
  } else if (error && items.length === 0) {
    content = <ErrorState error={error} onRetry={() => refetch()} />;
  } else if (items.length === 0) {
    content = (
      <EmptyState
        icon={BoxIcon}
        title="No products yet"
        description="Add your first product to start selling on Quro."
        action={
          <Button
            leftIcon={<PlusIcon className="h-4 w-4" />}
            onClick={() => navigate(ROUTES.CATALOG_ADD)}
          >
            Add product
          </Button>
        }
      />
    );
  } else {
    content = (
      <>
        <CatalogToolbar search={search} onSearchChange={setSearch} count={filteredItems.length} />
        <CatalogFilters
          items={items}
          category={category}
          onCategoryChange={setCategory}
          status={status}
          onStatusChange={setStatus}
        />
        {filteredItems.length === 0 ? (
          <EmptyState title="No matches" description="Try adjusting your filters or search." />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {filteredItems.map((item) => (
              <CatalogItemCard
                key={item.vendorCatalogId || item.productId}
                item={item}
                onClick={
                  item.vendorCatalogId
                    ? () => navigate(catalogEditPath(item.vendorCatalogId))
                    : undefined
                }
              />
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <div>
      <PageHeader title="Catalog" description="Products you sell on Quro." />
      {content}
    </div>
  );
}

export default CatalogPage;
